import { isApiError } from './errors';

export interface ApiErrorItem {
  code?: string;
  rules?: string[];
  lockedUntil?: string;
  retryAfterSec?: number;
  path?: string;
  msg?: string;
  methods?: string[];
  purpose?: string;
  addresses?: string[];
  activeFrom?: string;
  reason?: string;
  sentTo?: string;
  minPrefix?: number;
  currentAddress?: string;
}

export interface ParsedApiError {
  status?: number;
  code?: string;
  rules: string[];
  lockedUntil?: string;
  retryAfterSec?: number;
  fieldErrors: Record<string, string>;
  methods?: string[];
  purpose?: string;
  addresses?: string[];
  activeFrom?: string;
  reason?: string;
  sentTo?: string;
  minPrefix?: number;
  currentAddress?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readStrings = (value: unknown): string[] | undefined =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : undefined;

const readItem = (value: unknown): ApiErrorItem | null => {
  if (!isRecord(value)) {
    return null;
  }

  return {
    code: typeof value.code === 'string' ? value.code : undefined,
    rules: Array.isArray(value.rules) ? value.rules.filter((rule): rule is string => typeof rule === 'string') : undefined,
    lockedUntil: typeof value.lockedUntil === 'string' ? value.lockedUntil : undefined,
    retryAfterSec: typeof value.retryAfterSec === 'number' ? value.retryAfterSec : undefined,
    path: typeof value.path === 'string' ? value.path : undefined,
    msg: typeof value.msg === 'string' ? value.msg : undefined,
    methods: readStrings(value.methods),
    purpose: typeof value.purpose === 'string' ? value.purpose : undefined,
    addresses: readStrings(value.addresses),
    activeFrom: typeof value.activeFrom === 'string' ? value.activeFrom : undefined,
    reason: typeof value.reason === 'string' ? value.reason : undefined,
    sentTo: typeof value.sentTo === 'string' ? value.sentTo : undefined,
    minPrefix: typeof value.minPrefix === 'number' ? value.minPrefix : undefined,
    currentAddress: typeof value.currentAddress === 'string' ? value.currentAddress : undefined,
  };
};

export const readApiError = (error: unknown): ParsedApiError | null => {
  if (!isApiError(error) || !isRecord(error.data)) {
    return null;
  }

  const items = Array.isArray(error.data.errors)
    ? error.data.errors.map(readItem).filter((item): item is ApiErrorItem => item !== null)
    : [];
  const first = items[0];
  const code = first?.code;
  const fieldErrors: Record<string, string> = {};

  if (!code) {
    items.forEach((item) => {
      if (item.path) {
        fieldErrors[item.path] = 'recovery.fieldInvalid';
      }
    });
  }

  return {
    status: error.status,
    code,
    rules: first?.rules ?? [],
    lockedUntil: first?.lockedUntil,
    retryAfterSec: first?.retryAfterSec,
    fieldErrors,
    methods: first?.methods,
    purpose: first?.purpose,
    addresses: first?.addresses,
    activeFrom: first?.activeFrom,
    reason: first?.reason,
    sentTo: first?.sentTo,
    minPrefix: first?.minPrefix,
    currentAddress: first?.currentAddress,
  };
};
